import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Check, PartyPopper, Sparkles, Heart, Users, HandHeart } from "lucide-react";
import {
  CATEGORIES,
  PARTICIPATION_ROLES,
  NEIGHBORHOODS,
} from "@/lib/communityCategories";

const ICONS = { PartyPopper, Sparkles, Heart, Users, HandHeart };

/**
 * "Get Involved" — public / member community-interest sign-up page.
 * Casual, non-committee framing. Residents pick as many interest checkboxes as they like.
 * If they're logged in, we prefill first/last/email/phone and their linked property address.
 */
export default function GetInvolvedPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    neighborhood: "",
    street: "",
    activity_idea: "",
    skills: "",
  });
  const [interests, setInterests] = useState({}); // { [categoryKey]: [optionKey,…] }
  const [participation, setParticipation] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Prefill from logged-in user + their linked property (one-shot when user resolves).
  useEffect(() => {
    if (!user) return;
    const [f, ...rest] = (user.name || "").split(" ");
    setForm((s) => ({
      ...s,
      first_name: s.first_name || f || "",
      last_name: s.last_name || rest.join(" ") || "",
      email: s.email || user.email || "",
      phone: s.phone || user.phone || "",
    }));
    api.get("/portal/my-property")
      .then((r) => {
        if (!r.data) return;
        setForm((s) => ({
          ...s,
          street: s.street || r.data.address || "",
        }));
      })
      .catch(() => {});
  }, [user]);

  const toggleInterest = (catKey, optKey) => {
    setInterests((cur) => {
      const list = cur[catKey] || [];
      const next = list.includes(optKey) ? list.filter((k) => k !== optKey) : [...list, optKey];
      const copy = { ...cur };
      if (next.length === 0) delete copy[catKey];
      else copy[catKey] = next;
      return copy;
    });
  };
  const toggleParticipation = (key) => {
    setParticipation((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  const totalInterests = useMemo(
    () => Object.values(interests).reduce((n, arr) => n + arr.length, 0),
    [interests],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("First name, last name, and email are required");
      return;
    }
    if (totalInterests === 0 && participation.length === 0) {
      toast.error("Pick at least one interest or way to participate");
      return;
    }
    setBusy(true);
    try {
      await api.post("/community-interest", {
        ...form,
        interests,
        participation,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="get-involved-thanks">
        <div className="w-16 h-16 rounded-full bg-[#EADCC8] flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-[#2C4C3B]" />
        </div>
        <h1 className="font-serif text-4xl text-[#1A2421] mb-4">Thank you for getting involved!</h1>
        <p className="text-[#5C6A64] mb-8">
          Your interest has been submitted. We'll reach out as volunteer opportunities and activities come up.
        </p>
        <Link to="/">
          <Button className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="get-involved-page" className="max-w-4xl mx-auto px-6 py-16">
      {/* Header */}
      <div className="mb-10">
        <div className="w-14 h-14 rounded-full bg-[#EADCC8] flex items-center justify-center mb-4">
          <HandHeart className="w-7 h-7 text-[#C86650]" />
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Community</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421] mb-4 leading-tight">
          Get Involved in Our Community
        </h1>
        <p className="text-[#5C6A64] leading-relaxed text-base max-w-3xl">
          Help bring more connection, activities and fun to our community! Whether you'd like to help decorate,
          plan an event, lead a class or simply volunteer when you have time, choose the areas that interest you.
          There's no requirement to participate in every activity — we'll reach out when opportunities come up.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Category cards */}
        {CATEGORIES.map((cat) => {
          const Icon = ICONS[cat.icon] || HandHeart;
          const selectedInCat = interests[cat.key] || [];
          return (
            <Card
              key={cat.key}
              data-testid={`category-card-${cat.key}`}
              className="rounded-2xl border-[#D5CFC4] overflow-hidden"
            >
              <CardContent className="p-6 md:p-7">
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${cat.accent}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: cat.accent }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl text-[#1A2421]">{cat.title}</h3>
                      {selectedInCat.length > 0 && (
                        <span
                          data-testid={`category-count-${cat.key}`}
                          className="text-xs px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: cat.accent }}
                        >
                          {selectedInCat.length} selected
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#5C6A64] mt-1">{cat.description}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {cat.options.map((opt) => {
                    const active = selectedInCat.includes(opt.key);
                    return (
                      <label
                        key={opt.key}
                        data-testid={`opt-${cat.key}-${opt.key}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${active ? "bg-[#FDF9F1] border-[#C86650]" : "bg-white border-[#D5CFC4] hover:border-[#2C4C3B]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleInterest(cat.key, opt.key)}
                          className="w-4 h-4 accent-[#C86650]"
                        />
                        <span className="text-sm text-[#1A2421]">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Contact details */}
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6 md:p-7 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-1">Your info</p>
              <h3 className="font-serif text-2xl text-[#1A2421]">Contact details</h3>
              {user && (
                <p className="text-xs text-[#2C4C3B] mt-1">Pre-filled from your account — edit if needed.</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">First name *</label>
                <Input
                  data-testid="gi-first-name"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Last name *</label>
                <Input
                  data-testid="gi-last-name"
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Email *</label>
                <Input
                  data-testid="gi-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Phone</label>
                <Input
                  data-testid="gi-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Neighborhood</label>
                <Select value={form.neighborhood} onValueChange={(v) => setForm({ ...form, neighborhood: v })}>
                  <SelectTrigger data-testid="gi-neighborhood" className="mt-1 rounded-xl bg-white">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    {NEIGHBORHOODS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Street or address (optional)</label>
                <Input
                  data-testid="gi-street"
                  value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  className="mt-1 rounded-xl"
                  placeholder="e.g. 4856 W. Saddleridge Dr."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participation preferences */}
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6 md:p-7">
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-1">Preferences</p>
              <h3 className="font-serif text-2xl text-[#1A2421]">How Would You Like to Participate?</h3>
              <p className="text-sm text-[#5C6A64] mt-1">Pick as many as apply.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {PARTICIPATION_ROLES.map((r) => {
                const active = participation.includes(r.key);
                return (
                  <label
                    key={r.key}
                    data-testid={`participation-${r.key}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${active ? "bg-[#FDF9F1] border-[#C86650]" : "bg-white border-[#D5CFC4] hover:border-[#2C4C3B]"}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleParticipation(r.key)}
                      className="w-4 h-4 accent-[#C86650]"
                    />
                    <span className="text-sm text-[#1A2421]">{r.label}</span>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Open text — idea + skills */}
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6 md:p-7 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#1A2421]">
                Do you have an activity, event, class or community idea you'd like to lead?
              </label>
              <Textarea
                data-testid="gi-idea"
                rows={3}
                placeholder="Tell us your idea!"
                value={form.activity_idea}
                onChange={(e) => setForm({ ...form, activity_idea: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A2421]">
                Do you have any skills, talents or experience you'd like to share with the community?
              </label>
              <Textarea
                data-testid="gi-skills"
                rows={3}
                placeholder="For example: fitness instructor, DJ, event planning, decorating, photography, cooking, gardening, etc."
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                className="mt-2 rounded-xl"
              />
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          disabled={busy}
          data-testid="gi-submit"
          className="w-full sm:w-auto rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-12 px-10 text-base"
        >
          {busy ? "Submitting…" : "Sign Me Up"}
        </Button>
      </form>
    </div>
  );
}
